import { useCallback, useEffect, useMemo, useState } from "react";

import { useSearchVaspsInfinite } from "~/v2/api/hooks/useGetCrypto";
import type { Vasp } from "~/v2/api/rest/cryptoWallet";

export const MIN_QUERY_LENGTH = 2;

interface UseVaspSearchProps {
  accountId: string;
  searchEnabled: boolean;
  initialQuery?: string;
}

export const useVaspSearch = ({
  accountId,
  searchEnabled,
  initialQuery = "",
}: UseVaspSearchProps) => {
  const [query, setQuery] = useState(initialQuery);
  const [debouncedQuery, setDebouncedQuery] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useSearchVaspsInfinite({
      accountId,
      query: debouncedQuery,
      enabled:
        searchEnabled &&
        !!accountId &&
        debouncedQuery.length >= MIN_QUERY_LENGTH,
    });

  // `looseParse` hands back the raw payload when the envelope fails, so `vasps`
  // isn't structurally guaranteed despite the declared type.
  const results = useMemo(
    () =>
      (data?.pages ?? []).flatMap((page) =>
        Array.isArray(page.vasps) ? page.vasps : [],
      ),
    [data],
  );

  const onLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // True between keystroke and the debounced query catching up. Folded into
  // isLoading so the dropdown shows a skeleton through both phases instead of
  // flashing stale results between keystroke and fetch.
  const isDebouncing =
    query !== debouncedQuery && query.length >= MIN_QUERY_LENGTH;

  const reset = useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
  }, []);

  return {
    query,
    setQuery,
    results,
    isLoading: isLoading || isDebouncing,
    hasMore: !!hasNextPage,
    isLoadingMore: isFetchingNextPage,
    onLoadMore,
    reset,
  };
};

export type UseVaspSearchReturn = ReturnType<typeof useVaspSearch>;

/**
 * Composite state passed to presentation components. Built in the controller
 * by combining `useVaspSearch` (API) with form-owned selection state.
 */
export interface VaspSearchState {
  query: string;
  setQuery: (value: string) => void;
  results: Vasp[];
  isLoading: boolean;
  // Paging for the results list; the dropdown loads more on scroll-to-bottom.
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  selectedVasp: Vasp | null;
  isManualEntry: boolean;
  manualVaspName: string;
  manualVaspWebsite: string;
  setManualVaspName: (value: string) => void;
  setManualVaspWebsite: (value: string) => void;
  onSelectVasp: (vasp: Vasp) => void;
  onSelectManualEntry: () => void;
  onClearSelection: () => void;
  // Direct DID entry, for users who already know the beneficiary's VASP DID.
  // `didEntryName` only feeds the whitelist-level `institution_name`; it never
  // reaches `travel_rule_info`. Only the create endpoint accepts (and requires)
  // that field, so `didEntryNameRequired` is false when editing.
  isDidEntry: boolean;
  didEntryNameRequired: boolean;
  didEntryName: string;
  didEntryNameError?: string;
  onSelectDidEntry: (did: string) => void;
  setDidEntryName: (value: string) => void;
  onBlurDidEntryName: () => void;
}
